import { useEffect, useState } from 'react';
import { CreditCard, Plus, Star, Trash2 } from 'lucide-react';
import { Badge, Button, Callout, Card, CardBody, CardHead, Field, Input, Loading, Select, useToast } from './ui.jsx';
import { api } from '../lib/api.js';

/**
 * Which processor takes the money, and with whose keys.
 *
 * The design is easy3pl's, learned the hard way: the processor is a *record*,
 * so adding Square or moving to Stripe is a row and a key rather than a rewrite.
 * Keys can also live in the host's environment (STRIPE_SECRET_KEY,
 * SQUARE_ACCESS_TOKEN, QUICKBOOKS_CLIENT_SECRET…) — then they never sit in the
 * database at all, and this card says so.
 */
export default function PaymentProcessorsCard() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const payload = await api.get('/payments/processors');
      setData(payload.data);
    } catch (err) {
      toast.err('Could not load the payment settings', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    if (!draft?.provider) {
      toast.err('Pick a processor first');
      return;
    }
    setBusy(true);
    try {
      await api.post('/payments/processors', draft);
      setDraft(null);
      await load();
      toast.ok('Processor saved');
    } catch (err) {
      toast.err('That did not save', err.message);
    } finally {
      setBusy(false);
    }
  };

  const makeDefault = async (processor) => {
    setBusy(true);
    try {
      await api.post(`/payments/processors/${processor.id}/default`, {});
      await load();
      toast.ok(`${processor.name} takes the money from now on`);
    } catch (err) {
      toast.err('Could not change that', err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (processor) => {
    setBusy(true);
    try {
      await api.del(`/payments/processors/${processor.id}`);
      await load();
      toast.ok(`${processor.name} removed`);
    } catch (err) {
      toast.err('Could not remove that', err.message);
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (processor, enabled) => {
    setBusy(true);
    try {
      await api.patch(`/payments/processors/${processor.id}`, { enabled: enabled ? 1 : 0 });
      await load();
    } catch (err) {
      toast.err('Could not switch that', err.message);
    } finally {
      setBusy(false);
    }
  };

  const inUse = data?.inUse || null;

  return (
    <Card>
      <CardHead
        title="How customers pay"
        subtitle="Card processing, or the monthly bill"
        icon={CreditCard}
        actions={
          <Button size="sm" variant="outline" onClick={() => setDraft({ provider: 'stripe', useCase: 'job_charges', enabled: 1 })}>
            <Plus size={14} /> Add a processor
          </Button>
        }
      />
      <CardBody className="col gap-16">
        {loading ? (
          <Loading rows={2} />
        ) : (
          <>
            <Callout tone={inUse ? 'green' : 'amber'} icon={CreditCard} title={inUse ? `Cards are taken through ${inUse.provider}` : 'Customers are billed monthly'}>
              {inUse
                ? 'When a job is approved, the card on file is charged. If the charge fails, the job still gets approved — the office is told to chase it.'
                : 'Nothing is charged automatically. Jobs pile up on the customer’s invoice the way they always have. Add a processor to take cards when a job is approved.'}
            </Callout>

            {(data?.processors || []).length === 0 ? (
              <p className="muted">
                No processor is configured. You can also set the keys on the host instead — STRIPE_SECRET_KEY, SQUARE_ACCESS_TOKEN
                or QUICKBOOKS_CLIENT_SECRET — and they never touch this database.
              </p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Processor</th>
                      <th>For</th>
                      <th>Keys</th>
                      <th className="narrow">In use</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.processors.map((processor) => (
                      <tr key={processor.id}>
                        <td>
                          {processor.name}
                          {processor.enabled ? null : <Badge tone="muted" size="sm"> off</Badge>}
                        </td>
                        <td className="dim">{processor.provider} · {String(processor.useCase).replace('_', ' ')}</td>
                        <td className="dim">
                          {processor.envBacked ? 'from the host' : processor.hasSecret ? processor.secretKeyMasked : <span className="red">none yet</span>}
                          {processor.hasWebhookSecret ? null : <span className="dim"> · no webhook secret</span>}
                        </td>
                        <td className="narrow">
                          {processor.isDefault ? <Badge tone="green" size="sm"><Star size={11} /> default</Badge> : null}
                        </td>
                        <td className="right">
                          <div className="row gap-6 end">
                            <Button size="sm" variant="ghost" disabled={busy} onClick={() => toggle(processor, !processor.enabled)}>
                              {processor.enabled ? 'Turn off' : 'Turn on'}
                            </Button>
                            {processor.isDefault ? null : (
                              <Button size="sm" variant="outline" disabled={busy} onClick={() => makeDefault(processor)}>
                                Use this one
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" disabled={busy} onClick={() => remove(processor)}>
                              <Trash2 size={13} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {draft ? (
              <div className="tile col gap-12">
                <div className="grid grid-2" style={{ gap: 12 }}>
                  <Field label="What is it">
                    <Select
                      value={draft.provider}
                      onChange={(e) => setDraft({ ...draft, provider: e.target.value })}
                      options={(data?.providers || []).map((p) => ({ value: p.key, label: `${p.label} — ${p.takeRates}` }))}
                    />
                  </Field>
                  <Field label="Call it">
                    <Input value={draft.name || ''} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Stripe — main account" />
                  </Field>
                  <Field label="Secret key" hint="Roughly sk_live_… for Stripe, the access token for Square">
                    <Input className="mono" value={draft.secretKey || ''} onChange={(e) => setDraft({ ...draft, secretKey: e.target.value })} />
                  </Field>
                  <Field label="Webhook secret" hint="So the processor can tell us what happened">
                    <Input className="mono" value={draft.webhookSecret || ''} onChange={(e) => setDraft({ ...draft, webhookSecret: e.target.value })} />
                  </Field>
                </div>
                <div className="row gap-8 end">
                  <Button variant="ghost" onClick={() => setDraft(null)}>Cancel</Button>
                  <Button variant="primary" disabled={busy} onClick={save}>Save it</Button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </CardBody>
    </Card>
  );
}
